// @flow
// ----------------------------------------------------------------------
// <copyright file="Izenda.FileUpload.js" company="Izenda">
//  Copyright (c) 2015 Izenda, Inc.
//  ALL RIGHTS RESERVED
//
//  The entire contents of this file is protected by U.S. and
//  International Copyright Laws. Unauthorized reproduction,
//  reverse-engineering, and distribution of all or any portion of
//  the code contained in this file is strictly prohibited and may
//  result in severe civil and criminal penalties and will be
//  prosecuted to the maximum extent possible under the law.
//
//  RESTRICTIONS
//
//  THIS SOURCE CODE AND ALL RESULTING INTERMEDIATE FILES
//  ARE CONFIDENTIAL AND PROPRIETARY TRADE
//  SECRETS OF IZENDA INC. THE REGISTERED DEVELOPER IS
//  LICENSED TO DISTRIBUTE THE PRODUCT AND ALL ACCOMPANYING .NET
//  CONTROLS AS PART OF AN EXECUTABLE PROGRAM ONLY.
//
//  THE SOURCE CODE CONTAINED WITHIN THIS FILE AND ALL RELATED
//  FILES OR ANY PORTION OF ITS CONTENTS SHALL AT NO TIME BE
//  COPIED, TRANSFERRED, SOLD, DISTRIBUTED, OR OTHERWISE MADE
//  AVAILABLE TO OTHER INDIVIDUALS WITHOUT EXPRESS WRITTEN CONSENT
//  AND PERMISSION FROM IZENDA INC.
//
//  CONSULT THE END USER LICENSE AGREEMENT(EULA FOR INFORMATION ON
//  ADDITIONAL RESTRICTIONS.
//  </copyright>
// ----------------------------------------------------------------------

import React, { Component } from 'react';
import PropTypes from 'prop-types';
import classnames from "classnames";
import { endsWith, groupBy, isArray, isEmpty, map, some, join, toPairs } from 'lodash/fp';
import { observable } from 'mobx';

import fetcher from 'share/fetch/Izenda.Fetch';
import FileDropzone from 'share/components/hoc/Izenda.FileDropzone';
import FileUpload from 'share/components/controls/FileUpload/Izenda.FileUpload';
import ScrollBox from 'share/components/controls/Izenda.ScrollBox';
import Popup from 'share/components/controls/CommonPopup/Izenda.Popup';
import connect from 'share/enhancers/Izenda.Connect';

@connect()
export default class FileUploadWithDrag extends Component {
  static propTypes = {
    localization: PropTypes.object.isRequired,
    loader: PropTypes.object.isRequired,
    url: PropTypes.string.isRequired,
    params: PropTypes.object,
    fileExtentions: PropTypes.arrayOf(PropTypes.string),
    onSuccess: PropTypes.func,
    onError: PropTypes.func
  };

  @observable
  invalidFileMessages = [];

  /**
   * Show popup with files which couldn't be loaded and then clear list of those files.
   */
  openFileErrorPopup = (): Promise => {
    const { localization } = this.props;

    if (!isEmpty(this.invalidFileMessages)) {
      return this.props.openPopup(FileUploadErrorPopup, {
        localization: localization,
        messages: this.invalidFileMessages,
        onClosing: () => {
          this.invalidFileMessages = [];
        }
      });
    } else {
      Promise.resolve();
    }
  };

  /**
   * Run upload files query
   * @param files list of files.
   * @returns {Promise}
   */
  uploadFiles = (files: Array<object>): Promise<any> => {
    const { url, params } = this.props;

    const urlParams = join('&', map(pair => `${pair[0]}=${encodeURIComponent(pair[1])}`, toPairs(params)));
    const urlWithParams = `${url}?${urlParams}`;
    return fetcher(urlWithParams).upload(files)
  };

  validateAndUploadFiles(files: Array<object>): Promise<any> {
    const {fileExtentions, localization} = this.props;
    const groupByValidFiles =
      files => groupBy(
        file => some(ext => endsWith(ext, file.name), fileExtentions),
        files
      );
    const {true: validFiles, false: invalidFiles} = groupByValidFiles(files);

    // Show error message for the files which won't be loaded.
    const getInvalidMessages =
      files => map(
        file => {
          const fileName = file.name.replace(/(^.*[\\\/]|\.[^/.]+$)/, '');
          return `${fileName} - ${localization.getText('IMPORT_LABEL_INVALID_FILE_FORMAT')}`;
        }, files);
    this.invalidFileMessages = getInvalidMessages(invalidFiles);

    // Create executing promise
    let resultPromise;
    if (isEmpty(validFiles)) {
      resultPromise = Promise.reject(false);
    } else {
      resultPromise = this.uploadFiles(validFiles).then(content => {
        return content;
      });
    }

    // If we have some files with invalid extention: show modal before executing callback.
    if (!isEmpty(this.invalidFileMessages)) {
      return this.openFileErrorPopup().then(() => resultPromise);
    } else {
      return resultPromise;
    }
  }

  handleDropFiles = (files: Array<object>) => {
    const { loader, onError, onSuccess } = this.props;
    loader.startLoading();
    this.validateAndUploadFiles(files)
      .then(content => {
        onSuccess && onSuccess(isArray(content) ? content[0] : content);
      }, error => {
        onError && onError(error);
      })
      .catch(error => {
        onError && onError(error);
      })
      .finally(loader.endLoading.bind(loader));
  };

  renderUploadDragZone() {
    const {localization} = this.props;
    const classes = classnames('izenda-ImportFile-description');
    const renderDropZone = () => {
      return (
        <div className={classes}>{localization.getText('IMPORT_LABEL_DRAG_AND_DROP_FILES')}</div>
      );
    };
    return (
      <FileDropzone onDrop={this.handleDropFiles} render={renderDropZone}/>
    );
  }

  renderUploadButton() {
    const {url, params, fileExtentions, onSuccess, onError} = this.props;
    const options = {
      baseUrl: url,
      param: params || {},
      accept: join(',', fileExtentions || []),
      requestHeaders: fetcher().getAuthHeader(),
      chooseAndUpload: true,
      multiple: false,
      wrapperDisplay: 'inline',
      uploadSuccess: (content: any) => {
        onSuccess && onSuccess(content);
      },
      uploadError: (error) => {
        onError && onError(error);
      },
      uploadFail: (resp) => {
        onError && onError(resp.message);
      }
    };
    return (
      <FileUpload isHighlight={false} options={options}/>
    );
  }

  render() {
    const {localization} = this.props;
    return (
      <div className="u_flexVertical u_alignItemCenter u_flexJustifyCenter u_fullHeight">
        {this.renderUploadDragZone()}
        <span className="u_marginAll">{localization.getText('COMMON_LABEL_OR')}</span>
        {this.renderUploadButton()}
      </div>
    );
  }
}

@connect()
class FileUploadErrorPopup extends Component {
  static propTypes = {
    localization: PropTypes.object.isRequired,
    message: PropTypes.arrayOf(PropTypes.string)
  };

  onClosing = () => {
    this.props.onAccept();
  };

  renderMessages = (messages: Array<string>): any => {
    return (
      <ScrollBox autoHeight>
        {messages.map((item, index) => (
          <p key={index}>{item}</p>
        ))}
      </ScrollBox>
    );
  };

  render() {
    const { localization, messages } = this.props;
    return (
      <Popup visible onClosing={this.onClosing} type="none" autoClose={false} sizeClass="modal-lg">
        <Popup.Header title={localization.getText('IMPORT_LABEL_VALIDATION_ERRORS_FILES')} />
        <Popup.Body>{this.renderMessages(messages)}</Popup.Body>
        <Popup.Footer>
          <Popup.ConfirmationButton type='accept'>{localization.getText('COMMON_LABEL_OK')}</Popup.ConfirmationButton>
        </Popup.Footer>
      </Popup>
    );
  }
}



// WEBPACK FOOTER //
// ./ui/share/components/controls/FileUploadWithDrag/Izenda.FileUploadWithDrag.js